// Story 027 + 065 — workflow mutation hooks.
//
// All four hooks invalidate `analysisKeys.detail(id)` on success so the
// AnalysisHeader re-renders against the new `evaluationState`. They
// also append an audit-log row.
//
// Specs:
//   - 092 approve / reject data acquisition.
//   - 093 mark complete + review progress.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getIntakeBaseUrl } from "@aeriosiq/auth-client";
import type { DataAcquisitionDetail, IntakeComponents } from "@aeriosiq/types";
import { useAuditLogAppendMutation } from "../audit/queries.js";
import { analysisKeys } from "./queries.js";
import { dataAcquisitionKeys } from "../data-acquisitions/queries.js";

export const reviewProgressKey = (dataAcquisitionId: string) =>
  [...analysisKeys.detail(dataAcquisitionId), "review-progress"] as const;

export interface ReviewProgress {
  phase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | "EVALUATION_DONE" | null;
  viewedCount: number;
  total: number;
  viewedDefectIds: string[];
}

export function useReviewProgressQuery(dataAcquisitionId: string) {
  const api = useApi();
  return useQuery<ReviewProgress>({
    queryKey: reviewProgressKey(dataAcquisitionId),
    enabled: dataAcquisitionId.length > 0,
    queryFn: async () => {
      const response = await api.get<ReviewProgress>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/review-progress`,
      );
      return response.data;
    },
    // Live-fed by Story 024's focus-equals-review transitions — refetch
    // on every successful transition + DA detail invalidation.
    staleTime: 5_000,
    // Suppress 404 retries — the dev-api stub may not be restarted yet
    // and we don't want a chain of re-renders while the user is
    // interacting (Story 010 AC3 regression: the follow-up retries
    // stalled the RGL drag).
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export interface ApproveDataAcquisitionVars {
  dataAcquisitionId: string;
  acknowledgeUnseen: boolean;
}
export interface ApproveResponse {
  dataAcquisition: DataAcquisitionDetail;
  stateHistoryId: string;
}

export function useApproveDataAcquisitionMutation() {
  const api = useApi();
  const qc = useQueryClient();
  const audit = useAuditLogAppendMutation();
  return useMutation<
    ApproveResponse,
    Error & { errorId?: string },
    ApproveDataAcquisitionVars
  >({
    mutationFn: async (vars) => {
      const response = await api.post<ApproveResponse>(
        `/data-acquisitions/${encodeURIComponent(vars.dataAcquisitionId)}/approve`,
        { acknowledgeUnseen: vars.acknowledgeUnseen },
      );
      return response.data;
    },
    onSuccess: async (data, vars) => {
      qc.setQueryData(
        analysisKeys.detail(vars.dataAcquisitionId),
        data.dataAcquisition,
      );
      try {
        await audit.mutateAsync({
          entityType: "defect", // re-using existing enum slot; PR-D's audit
          // schema bump introduces "dataAcquisition".
          entityId: vars.dataAcquisitionId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { evaluationState: "EVALUATION_DONE" },
          after: {
            evaluationState: "LOCKED",
            acknowledgeUnseen: vars.acknowledgeUnseen,
          },
        });
      } catch (e: unknown) {
        console.warn("[027] approve audit-log append failed", e);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: analysisKeys.detail(vars.dataAcquisitionId),
      });
    },
  });
}

export interface RejectDataAcquisitionVars {
  dataAcquisitionId: string;
  reason: string;
  targetPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
}
export interface RejectResponse {
  dataAcquisition: DataAcquisitionDetail;
  stateHistoryId: string;
  reason: string;
  targetPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
}

export function useRejectDataAcquisitionMutation() {
  const api = useApi();
  const qc = useQueryClient();
  const audit = useAuditLogAppendMutation();
  return useMutation<
    RejectResponse,
    Error & { errorId?: string },
    RejectDataAcquisitionVars
  >({
    mutationFn: async (vars) => {
      const response = await api.post<RejectResponse>(
        `/data-acquisitions/${encodeURIComponent(vars.dataAcquisitionId)}/reject`,
        { reason: vars.reason, targetPhase: vars.targetPhase },
      );
      return response.data;
    },
    onSuccess: async (data, vars) => {
      qc.setQueryData(
        analysisKeys.detail(vars.dataAcquisitionId),
        data.dataAcquisition,
      );
      try {
        await audit.mutateAsync({
          entityType: "defect",
          entityId: vars.dataAcquisitionId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { evaluationState: "(prior)" },
          after: {
            evaluationState: "REJECTED",
            reason: vars.reason,
            targetPhase: vars.targetPhase,
          },
        });
      } catch (e: unknown) {
        console.warn("[027] reject audit-log append failed", e);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: analysisKeys.detail(vars.dataAcquisitionId),
      });
    },
  });
}

export interface MarkCompleteVars {
  dataAcquisitionId: string;
  fromPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | "EVALUATION_DONE";
}
export interface MarkCompleteResponse {
  dataAcquisition: DataAcquisitionDetail;
  stateHistoryId: string;
  fromPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | "EVALUATION_DONE";
  toPhase: "EXPERT_ANALYSIS" | "EVALUATION_DONE" | "LOCKED";
  completedAt: string;
}

export function useMarkDataAcquisitionCompleteMutation() {
  const api = useApi();
  const qc = useQueryClient();
  const audit = useAuditLogAppendMutation();
  return useMutation<
    MarkCompleteResponse,
    Error & { errorId?: string },
    MarkCompleteVars
  >({
    mutationFn: async (vars) => {
      const response = await api.post<MarkCompleteResponse>(
        `/data-acquisitions/${encodeURIComponent(vars.dataAcquisitionId)}/complete`,
        { fromPhase: vars.fromPhase },
      );
      return response.data;
    },
    onSuccess: async (data, vars) => {
      qc.setQueryData(
        analysisKeys.detail(vars.dataAcquisitionId),
        data.dataAcquisition,
      );
      // FOLLOWUP(story-063): when pop-out windows ship, dispatch the
      // `phase_changed` BroadcastChannel message HERE so every open
      // window re-renders against the new phase in lockstep. For PR-B
      // the dispatch is local (same-window) — the cache invalidation
      // is sufficient.
      try {
        const ch = new BroadcastChannel("aero.lyse.phase_changed");
        ch.postMessage({
          dataAcquisitionId: vars.dataAcquisitionId,
          fromPhase: data.fromPhase,
          toPhase: data.toPhase,
          completedAt: data.completedAt,
        });
        ch.close();
      } catch (e: unknown) {
        console.warn("[065] BroadcastChannel post failed", e);
      }
      try {
        await audit.mutateAsync({
          entityType: "defect",
          entityId: vars.dataAcquisitionId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { evaluationState: data.fromPhase },
          after: { evaluationState: data.toPhase },
        });
      } catch (e: unknown) {
        console.warn("[065] complete audit-log append failed", e);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: analysisKeys.detail(vars.dataAcquisitionId),
      });
      void qc.invalidateQueries({
        queryKey: reviewProgressKey(vars.dataAcquisitionId),
      });
    },
  });
}

// DI-3-R1-FE-analyst — "Send back to the pilot…" (the Analysis Tool's
// three-dots menu). NOT a lyse/v1 transition: the send-back is the intake
// domain's `reopen` (intake/v1 1.15.0, `reopenIntake`):
//   POST /api/intake/v1/data-acquisitions/{id}/intake/reopen  { note }
// The set goes back to CREATED / verdict_ready in the pilot's intake; its
// findings, points and images stay (DI-3-R1 4.6). The note is REQUIRED for
// an analyst role (3–500 chars after trimming, else 400 note_required).
//
// Base URL: `useApi()` is base-pinned to `/api/lyse/v1`, so — the same
// cross-namespace pattern as `queries.componentGroups.ts` (assets/v1) and
// `useSuggestAiMapMutation.ts` (taxonomy/v1) — the request carries a
// per-request `baseURL` override from `getIntakeBaseUrl()` (wired by DI-2-4:
// `VITE_INTAKE_BASE_URL` / runtime `intakeBaseUrl`, default `/api/intake/v1`).
// The shared axios instance still adds the bearer + X-Tenant-ID.
//
// Types: the intake gen (`packages/types/src/api.intake.gen.ts`, pinned to
// intake/v1 1.15.0 by DI-3-R1-FE-pilot #484) — `ReopenRequest` /
// `ReopenResult` / `IntakeSentBack`; no local copy.

export const SEND_BACK_NOTE_MIN = 3;
export const SEND_BACK_NOTE_MAX = 500;

export interface SendBackToPilotVars {
  dataAcquisitionId: string;
  note: string;
  /** The set's state when the analyst clicked — for the client audit row. */
  fromState: string | null;
}

/** intake/v1 1.15.0 `ReopenRequest`. */
export type SendBackToPilotRequest =
  IntakeComponents["schemas"]["ReopenRequest"];
/** intake/v1 1.15.0 `ReopenResult` (`sentBack` = `IntakeSentBack`). */
export type SendBackToPilotResponse =
  IntakeComponents["schemas"]["ReopenResult"];

export const SEND_BACK_SUCCESS_TEXT =
  "Sent back to the pilot — it will reappear in their intake";

/**
 * The words for a refused send-back. The intake 409 / 400 envelopes carry
 * `{code, reason}`; the shared axios client wraps the error as an
 * `AeroApiError` whose `cause` is the AxiosError with the body.
 */
export function sendBackRefusalText(err: unknown): string {
  const e = err as {
    status?: number;
    cause?: { response?: { status?: number; data?: { reason?: unknown } } };
  } | null;
  const status = e?.status ?? e?.cause?.response?.status;
  const reason = e?.cause?.response?.data?.reason;
  if (status === 409 && reason === "evaluation_done") {
    return "This data set's evaluation is finished — it cannot be sent back";
  }
  if (status === 409 && reason === "not_approved") {
    return "This data set is not in analysis yet";
  }
  if (status === 403) {
    return "You cannot send data sets back";
  }
  return "Could not send back — please retry";
}

export function useSendBackToPilotMutation() {
  const api = useApi();
  const qc = useQueryClient();
  const audit = useAuditLogAppendMutation();
  return useMutation<
    SendBackToPilotResponse,
    Error & { errorId?: string },
    SendBackToPilotVars
  >({
    mutationFn: async (vars) => {
      const body: SendBackToPilotRequest = { note: vars.note };
      const response = await api.post<SendBackToPilotResponse>(
        `/data-acquisitions/${encodeURIComponent(vars.dataAcquisitionId)}/intake/reopen`,
        body,
        { baseURL: getIntakeBaseUrl() },
      );
      return response.data;
    },
    onSuccess: async (data, vars) => {
      try {
        await audit.mutateAsync({
          entityType: "defect", // the reject / approve slot (see above)
          entityId: vars.dataAcquisitionId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { evaluationState: vars.fromState ?? "(prior)" },
          after: {
            evaluationState: "CREATED",
            sentBackToPilot: true,
            note: vars.note,
            instructionState: data.instructionState,
          },
        });
      } catch (e: unknown) {
        console.warn("[DI-3-R1] send-back audit-log append failed", e);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: analysisKeys.detail(vars.dataAcquisitionId),
      });
      void qc.invalidateQueries({ queryKey: dataAcquisitionKeys.all });
    },
  });
}
